/* eslint-disable no-nested-ternary */
import { keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { requestGetPromotionProducts } from '@apis/renewal/promotion';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import { PromotionContent } from '@wconcept/wconcept-common/models/renewal/promotion';
import usePromotionVisibilityStore, {
  ModuleProductData as PersistedModuleProductData,
  QueryStatus as PersistedQueryStatus,
} from '@store/promotionVisibilityStore';
import { useVisibleModules } from './useVisibleModules';
import { useWckQueries } from './wckQuery/useWckQueries';

// 배열을 지정된 크기로 나누는 유틸리티 함수
const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

interface UseVisiblePromotionProductsParams {
  contents: PromotionContent[];
  enabled?: boolean;
  rootMargin?: string;
  threshold?: number;
  initialVisibleThroughOrder?: number;
}

interface ModuleProductData {
  moduleType: string;
  contentId: string;
  itemCodes: string[];
  products: Product[];
  itemStatusCd?: Array<'01' | '02' | '04' | '05'>;
}

export interface QueryStatus {
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  error: unknown;
  productsMap?: Map<string, Product>;
}

export const useVisiblePromotionProducts = ({
  contents,
  enabled = true,
  rootMargin = '800px',
  threshold = 0.01,
  initialVisibleThroughOrder,
}: UseVisiblePromotionProductsParams) => {
  // 전역 스토어를 사용하여 shallow 네비게이션 간에도 누적 데이터 유지
  const {
    accumulatedModuleData,
    accumulatedQueryStatus,
    mergeModuleData,
    mergeQueryStatus,
    setContentsKey,
  } = usePromotionVisibilityStore();

  const { visibleItemCodes, registerModule, isModuleVisible } =
    useVisibleModules({
      contents,
      rootMargin,
      threshold,
      initialVisibleThroughOrder,
    });
  // 보이는 모듈들의 모든 상품 코드 수집
  const allVisibleItemCodes = Object.values(visibleItemCodes).flatMap(
    data => data.itemCodes
  );

  const uniqueVisibleItemCodes = [...new Set(allVisibleItemCodes)];

  // 보이는 영역 ID들 (쿼리키용) - 중복 방지를 위해 정렬된 문자열로 변환
  const visibleAreaIds = Object.keys(visibleItemCodes).sort();

  // 각 영역별로 개별 쿼리 생성 (useWckQueries 사용)
  const areaQueries = useWckQueries(
    visibleAreaIds.map(contentId => {
      const data = visibleItemCodes[contentId];
      const isEnabled = enabled && data && data.itemCodes.length > 0;

      // console.log('📋 쿼리 설정:', {
      //   contentId,
      //   data,
      //   isEnabled,
      //   itemCodesLength: data?.itemCodes?.length || 0,
      // });

      return {
        queryKey: ['visible-promotion-products', contentId],
        queryFn: async () => {
          // console.log('🚀 API 호출 시작:', {
          //   contentId,
          //   itemCodesLength: data?.itemCodes?.length || 0,
          //   itemCodes: data?.itemCodes?.slice(0, 5) || [],
          // });

          if (!data || data.itemCodes.length === 0) {
            // console.log('❌ 쿼리 실행 안됨 - 데이터 없음:', {
            //   contentId,
            //   data,
            // });
            return [];
          }

          // 30개씩 나눠서 배치 생성
          const batches = chunkArray(data.itemCodes, 30);
          // console.log('📦 배치 생성:', {
          //   contentId,
          //   totalItems: data.itemCodes.length,
          //   batchCount: batches.length,
          //   batchSizes: batches.map(batch => batch.length),
          // });

          // 모든 배치를 병렬로 호출
          const batchPromises = batches.map(async batch => {
            // console.log(`🔄 배치 호출:`, {
            //   contentId,
            //   batchSize: batch.length,
            //   batchItems: batch.slice(0, 3),
            // });

            const response = await requestGetPromotionProducts({
              itemCdList: batch,
              itemStatusCdList: data.itemStatusCd, // 각 영역의 itemStatusCd 전달
            });
            // console.log(`✅ 배치 완료:`, {
            //   contentId,
            //   responseLength: response?.productList?.length || 0,
            // });

            return response?.productList || [];
          });

          // 모든 배치 결과를 기다리고 합치기
          const batchResults = await Promise.all(batchPromises);
          const allProducts = batchResults.flat();

          // console.log('🎉 모든 배치 완료:', {
          //   contentId,
          //   totalProducts: allProducts.length,
          // });

          return allProducts;
        },
        enabled: isEnabled,
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000 * 5,
        gcTime: 60 * 1000 * 10,
        initialDataEnabled: true,
        refetchOnWindowFocus: false, // 뒤로가기 시 불필요한 refetch 방지
        refetchOnMount: false, // 마운트 시 refetch 방지 (캐시된 데이터 우선 사용)
      };
    })
  );

  // 모든 영역의 상품 데이터 합치기
  const products = areaQueries.flatMap(query => {
    const { data } = query;
    if (!data) return [];
    return Array.isArray(data) ? data : [];
  });

  // 뒤로가기 시 캐시된 데이터가 있으면 로딩 상태를 false로 설정
  const isLoading = useMemo(() => {
    // 현재 보이는 영역들에 대해 캐시된 데이터가 있는지 확인
    const hasCachedData = visibleAreaIds.some(contentId => {
      const cachedStatus = accumulatedQueryStatus.get(contentId);
      return (
        cachedStatus?.isSuccess &&
        cachedStatus.productsMap &&
        cachedStatus.productsMap.size > 0
      );
    });

    // 캐시된 데이터가 있으면 로딩 상태를 false로 설정
    if (hasCachedData) {
      return false;
    }

    // 캐시된 데이터가 없으면 실제 쿼리 상태 확인
    return areaQueries.some(query => query.isLoading);
  }, [visibleAreaIds, accumulatedQueryStatus, areaQueries]);

  const isError = areaQueries.some(query => query.isError);
  const error = areaQueries.find(query => query.error)?.error;

  // 상품 Map 생성 (누적 데이터 기반으로 유지)
  const productMap = new Map<string, Product>();
  const accumulatedProducts = Array.from(
    accumulatedModuleData.values()
  ).flatMap(v => v.products);
  accumulatedProducts.forEach(product => {
    if (product.itemCd) {
      productMap.set(product.itemCd, product);
    }
  });

  // 각 보이는 모듈별로 상품 데이터 매핑 (현재 렌더용)
  const moduleProductDataMap = new Map<string, ModuleProductData>();
  const moduleUpdates = new Map<string, PersistedModuleProductData>();
  areaQueries.forEach((query, index) => {
    const contentId = visibleAreaIds[index];
    const originalData = visibleItemCodes[contentId];

    if (originalData) {
      const { data } = query;
      const moduleData: ModuleProductData = {
        ...originalData,
        products: Array.isArray(data) ? data : [],
      };
      moduleProductDataMap.set(contentId, moduleData);
      moduleUpdates.set(contentId, moduleData as PersistedModuleProductData);
    }
  });

  // 현재 보이는 영역의 쿼리 상태 (렌더에서 계산, 커밋은 effect에서)
  const statusUpdates = new Map<string, PersistedQueryStatus>();
  areaQueries.forEach((query, index) => {
    const contentId = visibleAreaIds[index];
    const moduleData = moduleProductDataMap.get(contentId);

    // products 배열을 Map으로 변환 (itemCd를 key로)
    const productsMap = new Map<string, Product>();
    moduleData?.products?.forEach(product => {
      productsMap.set(product.itemCd, product);
    });

    // 뒤로가기 시 캐시된 데이터가 있으면 성공 상태로 설정
    const cachedStatus = accumulatedQueryStatus.get(contentId);
    const hasCachedData =
      cachedStatus?.isSuccess &&
      cachedStatus.productsMap &&
      cachedStatus.productsMap.size > 0;

    const queryStatus: PersistedQueryStatus = {
      isLoading: hasCachedData ? false : query.isLoading,
      isError: query.isError,
      isFetching: query.isFetching,
      isSuccess: hasCachedData ? true : query.isSuccess,
      error: query.error,
      productsMap:
        productsMap.size > 0
          ? productsMap
          : hasCachedData
          ? cachedStatus?.productsMap
          : undefined, // Map으로 저장
    };
    statusUpdates.set(contentId, queryStatus);
  });

  // 렌더 중 setState 방지: 전역 스토어 머지는 effect에서 수행
  const moduleUpdatesSig = useMemo(
    () =>
      Array.from(moduleUpdates.entries())
        .map(([id, m]) => {
          const productsLen = m.products ? m.products.length : 0;
          const itemCodesLen = m.itemCodes ? m.itemCodes.length : 0;
          return `${id}:${productsLen}:${itemCodesLen}`;
        })
        .join('|'),
    [moduleUpdates]
  );
  const statusUpdatesSig = useMemo(
    () =>
      Array.from(statusUpdates.entries())
        .map(([id, s]) => {
          const l = s.isLoading ? 1 : 0;
          const f = s.isFetching ? 1 : 0;
          const su = s.isSuccess ? 1 : 0;
          const e = s.isError ? 1 : 0;
          return `${id}:${l}:${f}:${su}:${e}`;
        })
        .join('|'),
    [statusUpdates]
  );

  useEffect(() => {
    if (moduleUpdates.size > 0) {
      mergeModuleData(moduleUpdates);
    }
    if (statusUpdates.size > 0) {
      mergeQueryStatus(statusUpdates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleUpdatesSig, statusUpdatesSig]);

  // contentsKey 설정해 콘텐츠 변경 감지 (빈 콘텐츠일 때는 리셋하지 않음)
  const contentsKey = useMemo(
    () =>
      contents
        .map(c => c.contentId)
        .sort()
        .join(','),
    [contents]
  );
  // 콘텐츠 세트가 바뀌면 스토어가 내부적으로 초기화되도록 키 전달
  // 일시적 빈 상태로 인한 리셋을 피하기 위해 키가 존재할 때만 설정
  useEffect(() => {
    if (contentsKey) {
      setContentsKey(contentsKey);
    }
  }, [contentsKey, setContentsKey]);

  // 누적된 쿼리 상태/모듈 데이터를 스토어에서 참조로 반환
  const queryStatusMap = useMemo(
    () => new Map(accumulatedQueryStatus),
    [accumulatedQueryStatus]
  );
  const accumulatedModuleDataMap = useMemo(
    () => new Map(accumulatedModuleData),
    [accumulatedModuleData]
  );

  // contentId로 직접 useQuery 상태를 가져올 수 있는 Map 생성
  const directQueryStatusMap = useMemo(() => {
    const map = new Map<
      string,
      {
        isLoading: boolean;
        isError: boolean;
        isFetching: boolean;
        isSuccess: boolean;
        error: unknown;
        data: Product[] | undefined;
      }
    >();

    areaQueries.forEach((query, index) => {
      const contentId = visibleAreaIds[index];
      map.set(contentId, {
        isLoading: query.isLoading,
        isError: query.isError,
        isFetching: query.isFetching,
        isSuccess: query.isSuccess,
        error: query.error,
        data: Array.isArray(query.data) ? query.data : undefined,
      });
    });

    return map;
  }, [areaQueries, visibleAreaIds]);

  return {
    moduleProductDataMap, // 현재 보이는 영역만
    accumulatedModuleDataMap, // 누적된 모든 영역
    productMap,
    queryStatusMap, // 누적된 모든 쿼리 상태 (하위 호환성)
    directQueryStatusMap, // 직접적인 useQuery 상태 (권장)
    isLoading,
    isError,
    error,
    registerModule,
    isModuleVisible,
    visibleItemCodes,
    totalItemCodes: uniqueVisibleItemCodes.length,
    loadedProducts: products?.length || 0,
  };
};
